/**
 * Partner — a reseller / brand organization in the Customer Portal.
 *
 * Hand-authored domain types for now. When `customer-portal-services` exists
 * and publishes an OpenAPI schema package (`@tveyes/customer-portal-schema`,
 * see Phase 0 plan T12), the wire-shaped subset of these should be replaced
 * by `components["schemas"]["X"]` re-exports the way `twosion-ui/src/models/*`
 * does, and the mapper in `src/features/metrics/metrics.api.ts` updated to
 * translate the generated type into this domain type.
 */

import type { ApiKind } from "./api";
import type { RegionKey } from "./tier";

export type TierId = "starter" | "growth" | "enterprise";

export type PartnerFlagType = "success" | "warning" | "error";

/** The single headline flag shown against a partner (from the POC `flag` field). */
export interface PartnerFlag {
  label: string;
  type: PartnerFlagType;
}

/** Used vs contracted call volume for one API, over the trailing 30 days. */
export interface ApiUsage {
  used: number;
  cap: number;
}

/** Activity outcomes shared by the model and filter options. */
export const ACTIVITY_STATUSES = ["Hit", "Error"] as const;

/**
 * One request/execution against a partner's Saved Search or Ad Hoc API.
 * `resultCount` is `null` only for a non-search API (e.g. Firehose's
 * continuous stream has no "result count"); a search that errored still
 * reports `0`, since an error genuinely returned no results.
 */
export interface PartnerActivityEntry {
  time: string;
  type: ApiKind;
  query: string;
  region: RegionKey;
  resultCount: number | null;
  status: (typeof ACTIVITY_STATUSES)[number];
}

/**
 * One day of volume, split by API; `null` is no data (overview D5). Firehose
 * is a continuous stream, not a per-call volume, so it's excluded.
 */
export interface VolumeTrendDay {
  /** 1-based position, oldest first. */
  day: number;
  saved: number | null;
  adhoc: number | null;
}

/** Daily volume, oldest first, as sent (30 days expected); feeds the deep dive's trend chart. */
export type VolumeTrend = VolumeTrendDay[];

export interface PartnerUsage {
  saved: ApiUsage;
  adhoc: ApiUsage;
  /** Whether the partner has the Firehose API enabled at all. */
  firehoseEnabled: boolean;
  /** Media-player video plays in the trailing 30 days. */
  mediaPlayerViews30d: number;
  /**
   * Trailing daily total call volume (Saved + Ad Hoc combined), oldest first.
   * The metrics layer derives N-day growth from this; the backend may instead
   * return a pre-computed growth figure, in which case this becomes optional
   * and `computeGrowth` is bypassed.
   */
  callVolumeSeries: number[];
  /** Trailing 30-day volume, split Saved vs Ad Hoc — see `VolumeTrend`. */
  volumeTrend: VolumeTrend;
  /** Recent per-request outcomes, newest first — drives the error-rate metric. */
  recentActivity: PartnerActivityEntry[];
}

export interface Partner {
  id: string;
  name: string;
  /** Two-letter monogram for the avatar. */
  initials: string;
  partnerId: string;
  tierId: TierId;
  accountManager: string;
  /** Annual contract value, in dollars. Formatted for display by `metricsFormat`. */
  acv: number;
  /** Year the partner came on, e.g. "2019". */
  since: string;
  /** ISO date, e.g. "2027-01-15". */
  renewalDate: string;
  flag: PartnerFlag;
  usage: PartnerUsage;
}
